import { Truck } from 'lucide-react';
import type { FilterState } from '../dashboardFilterUtils';
import { countryDisplayNames } from '../dashboardFilterUtils';
import SearchableSelect from '../../ui/SearchableSelect';
import { FilterSection, FilterGrid, FilterField, filterControlClass } from '../../filters/FilterSection';

interface SupplierSectionProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  suppliers: Array<{ id: number; name: string; group?: number | null; country?: string | null }>;
  supplierGroups: Array<{ id: number; name: string }>;
  supplierCountryOptions: string[];
  onSupplierCountryChange: (value: string) => void;
  /**
   * CR-6402 rework: on the supplier portal this group is exactly the Supplier
   * View design — Supplier group, Supplier country, Supplier site. Supplier
   * code and the Strategic-only tickbox are customer-portal filters and are
   * not rendered there (QA, 2026-08-07). Supplier country, which CR-4887 had
   * hidden from suppliers, is required by that same design and QA comment.
   */
  supplierPortal: boolean;
  /** CR-4800: group / site / code are purchasing concepts, not compliance ones. */
  showPurchasingFilters: boolean;
  /** CR-4802: Supplier Code only when the tenant holds supplier codes. */
  showSupplierCode?: boolean;
}

/**
 * CR-6401: SUPPLIER group — supplier-side selectors plus the strategic-only
 * tickbox, which previously sat two rows away from the filters it qualifies.
 *
 * Both existing cascades are preserved verbatim: choosing a Supplier Group
 * clears a site that no longer belongs to it (CR-4885), and the site list is
 * narrowed by group AND country (CR-6352).
 */
export function SupplierSection({
  filters,
  setFilters,
  suppliers,
  supplierGroups,
  supplierCountryOptions,
  onSupplierCountryChange,
  supplierPortal,
  showPurchasingFilters,
  showSupplierCode = true,
}: SupplierSectionProps) {
  const showCustomerOnlyFilters = showPurchasingFilters && !supplierPortal;
  return (
    <FilterSection title="Supplier" icon={Truck}>
      <FilterGrid>
        {showPurchasingFilters && (
          <FilterField label="Supplier group">
            <SearchableSelect
              options={supplierGroups.map((g) => ({ value: String(g.id), label: g.name }))}
              value={filters.supplierGroup}
              onChange={(val) => {
                // CR-4885: drop a site that isn't in the newly chosen group,
                // otherwise the dashboard reads an impossible filter combo.
                const next = { ...filters, supplierGroup: val };
                if (val && filters.supplier) {
                  const stillValid = suppliers.some(
                    (s) => String(s.id) === String(filters.supplier) && String(s.group ?? '') === val
                  );
                  if (!stillValid) next.supplier = '';
                }
                setFilters(next);
              }}
              placeholder="All supplier groups"
            />
          </FilterField>
        )}

        <FilterField label="Supplier country">
          <select
            value={filters.supplierCountry}
            onChange={(e) => onSupplierCountryChange(e.target.value)}
            className={filterControlClass}
          >
            <option value="">All countries</option>
            {supplierCountryOptions.map((countryName) => (
              <option key={countryName} value={countryName}>
                {countryDisplayNames[countryName] || countryName}
              </option>
            ))}
          </select>
        </FilterField>

        {showPurchasingFilters && (
          <FilterField label="Supplier site">
            <SearchableSelect
              // CR-4885 + CR-6352: narrowed by the selected group AND country.
              options={suppliers
                .filter((s) => !filters.supplierGroup || String(s.group ?? '') === filters.supplierGroup)
                .filter((s) => !filters.supplierCountry || s.country === filters.supplierCountry)
                .map((s) => ({ value: String(s.id), label: s.name }))}
              value={filters.supplier}
              onChange={(val) => setFilters({ ...filters, supplier: val })}
              placeholder="All supplier sites"
            />
          </FilterField>
        )}

        {showCustomerOnlyFilters && showSupplierCode && (
          <FilterField label="Supplier code">
            <input
              type="text"
              value={filters.supplierCode}
              onChange={(e) => setFilters({ ...filters, supplierCode: e.target.value })}
              placeholder="Enter supplier code"
              className={filterControlClass}
            />
          </FilterField>
        )}
      </FilterGrid>

      {showCustomerOnlyFilters && (
        <label className="flex items-center gap-2 cursor-pointer mt-4 w-fit">
          <input
            type="checkbox"
            checked={filters.strategicOnly}
            onChange={(e) => setFilters({ ...filters, strategicOnly: e.target.checked })}
            className="w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-2"
          />
          <span className="text-sm font-medium text-foreground">Strategic suppliers only</span>
        </label>
      )}
    </FilterSection>
  );
}
